import { useState } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Loading, EmptyState } from "@/components/common/PageHeader";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { ChevronUp, ChevronDown, Search, Trash2, ChevronLeft, ChevronRight, Inbox } from "lucide-react";
import { cn } from "@/lib/utils";

export function DataTable({
  columns, rows = [], loading, total = 0, page = 1, pageSize = 20, onPageChange,
  sortBy, sortDir = "desc", onSort, selectable, selectedIds = [], onSelectedChange,
  onBulkDelete, rowActions, search = "", onSearch, searchPlaceholder = "Search…",
  toolbar, emptyTitle, emptyHint, getId = (r) => r.id, testId = "data-table",
}) {
  const [confirmBulk, setConfirmBulk] = useState(false);
  const allSelected = rows.length > 0 && rows.every((r) => selectedIds.includes(getId(r)));
  const pages = Math.max(1, Math.ceil(total / pageSize));

  const toggleAll = () => {
    if (allSelected) onSelectedChange(selectedIds.filter((id) => !rows.some((r) => getId(r) === id)));
    else onSelectedChange([...new Set([...selectedIds, ...rows.map(getId)])]);
  };
  const toggleOne = (id) =>
    onSelectedChange(selectedIds.includes(id) ? selectedIds.filter((x) => x !== id) : [...selectedIds, id]);

  const SortIcon = ({ col }) => {
    if (sortBy !== col) return <ChevronDown className="h-3 w-3 opacity-20" />;
    return sortDir === "desc" ? <ChevronDown className="h-3 w-3" /> : <ChevronUp className="h-3 w-3" />;
  };

  return (
    <div className="space-y-3" data-testid={testId}>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-1 items-center gap-2">
          {onSearch && (
            <div className="relative w-full max-w-xs">
              <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                data-testid="table-search"
                value={search}
                onChange={(e) => onSearch(e.target.value)}
                placeholder={searchPlaceholder}
                className="pl-8"
              />
            </div>
          )}
          {toolbar}
        </div>
        {selectable && selectedIds.length > 0 && (
          <div className="flex items-center gap-2 rounded-sm border border-border bg-secondary px-3 py-1.5 text-sm">
            <span className="font-medium" data-testid="selected-count">{selectedIds.length} selected</span>
            {onBulkDelete && (
              <Button size="sm" variant="destructive" data-testid="bulk-delete-btn" onClick={() => setConfirmBulk(true)}>
                <Trash2 className="mr-1 h-3.5 w-3.5" /> Delete
              </Button>
            )}
          </div>
        )}
      </div>

      <div className="overflow-x-auto rounded-sm border border-border bg-card">
        <Table>
          <TableHeader>
            <TableRow className="border-border bg-secondary/60 hover:bg-secondary/60">
              {selectable && (
                <TableHead className="w-10">
                  <Checkbox checked={allSelected} onCheckedChange={toggleAll} data-testid="select-all" aria-label="Select all" />
                </TableHead>
              )}
              {columns.map((c) => (
                <TableHead
                  key={c.key}
                  className={cn("whitespace-nowrap text-xs uppercase tracking-wider text-muted-foreground", c.className, c.sortable && onSort && "cursor-pointer select-none")}
                  onClick={() => c.sortable && onSort && onSort(c.key)}
                >
                  <span className="inline-flex items-center gap-1">{c.label}{c.sortable && onSort && <SortIcon col={c.key} />}</span>
                </TableHead>
              ))}
              {rowActions && <TableHead className="w-10" />}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={columns.length + 2}><Loading /></TableCell></TableRow>
            ) : rows.length === 0 ? (
              <TableRow><TableCell colSpan={columns.length + 2}><EmptyState icon={Inbox} title={emptyTitle || "No records"} hint={emptyHint} /></TableCell></TableRow>
            ) : (
              rows.map((r) => {
                const id = getId(r);
                const sel = selectedIds.includes(id);
                return (
                  <TableRow key={id} data-testid={`row-${id}`} className={cn("border-border transition-colors hover:bg-secondary/40", sel && "bg-primary/5")}>
                    {selectable && (
                      <TableCell><Checkbox checked={sel} onCheckedChange={() => toggleOne(id)} data-testid={`select-${id}`} /></TableCell>
                    )}
                    {columns.map((c) => (
                      <TableCell key={c.key} className={cn("py-2.5", c.cellClassName)}>
                        {c.render ? c.render(r) : (r[c.key] ?? "—")}
                      </TableCell>
                    ))}
                    {rowActions && <TableCell className="text-right">{rowActions(r)}</TableCell>}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {total > 0 && (
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span data-testid="table-total">{total} record{total !== 1 ? "s" : ""}</span>
          {onPageChange && pages > 1 && (
            <div className="flex items-center gap-2">
              <Button size="icon" variant="outline" className="h-8 w-8" disabled={page <= 1} onClick={() => onPageChange(page - 1)} data-testid="page-prev">
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="tabular">Page {page} / {pages}</span>
              <Button size="icon" variant="outline" className="h-8 w-8" disabled={page >= pages} onClick={() => onPageChange(page + 1)} data-testid="page-next">
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      )}

      <ConfirmDialog
        open={confirmBulk}
        onOpenChange={setConfirmBulk}
        title={`Delete ${selectedIds.length} record(s)?`}
        description="This action cannot be undone. All selected records will be permanently removed and logged in the audit trail."
        confirmText="Delete all"
        destructive
        onConfirm={() => { setConfirmBulk(false); onBulkDelete(); }}
      />
    </div>
  );
}
